import React, { useState, useEffect } from 'react';
import { X, Check, Zap, TrendingUp, Users, Download, ArrowRight, Gift } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import subscriptionService from '../../services/subscriptionService';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { trackFeatureGateModalOpen, trackFeatureGateUpgradeClick, trackFeatureGateReferClick } from '../../hooks/useGoogleAnalytics';

const FeatureGateModal = ({ 
  isOpen, 
  onClose, 
  featureName, 
  featureDescription,
  currentUsage,
  limit,
  onUpgrade 
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [referralCount, setReferralCount] = useState(0);
  const [referralLoading, setReferralLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadPlans();
      loadReferralCount();
      // Fire GA event when modal opens
      trackFeatureGateModalOpen(featureName || 'unknown');
    }
  }, [isOpen, user]);

  const loadPlans = async () => {
    setLoading(true);
    const { data } = await subscriptionService?.getAvailablePlans();
    const premiumPlans = data?.filter(plan => plan?.planType !== 'basic') || [];
    setPlans(premiumPlans);
    setLoading(false);
  };

  const loadReferralCount = async () => {
    if (!user) { setReferralLoading(false); return; }
    setReferralLoading(true);
    try {
      const { data } = await supabase
        ?.from('referrals')
        ?.select('id')
        ?.eq('referrer_id', user?.id)
        ?.eq('status', 'converted');
      setReferralCount(data?.length || 0);
    } catch (e) {
      setReferralCount(0);
    } finally {
      setReferralLoading(false);
    }
  };

  const getFeatureIcon = () => {
    if (featureName?.includes('export')) return <Download className="w-12 h-12" />;
    if (featureName?.includes('caregiver')) return <Users className="w-12 h-12" />;
    if (featureName?.includes('analytics')) return <TrendingUp className="w-12 h-12" />;
    return <Zap className="w-12 h-12" />;
  };

  const getFeatureBenefits = () => {
    const benefits = {
      export: [
        'Unlimited monthly exports',
        'Multiple export formats (PDF, CSV, Word)',
        'Professional doctor reports',
        'Custom date range selection'
      ],
      caregiver: [
        'Connect with multiple caregivers',
        'Real-time health alerts',
        'Shared family dashboard',
        'Activity timeline tracking'
      ],
      analytics: [
        'Advanced symptom analytics',
        'Trend visualization',
        'Pattern recognition',
        'Detailed health insights'
      ],
      default: [
        'Unlimited feature access',
        'Priority support',
        'Advanced capabilities',
        'No monthly limits'
      ]
    };

    if (featureName?.includes('export')) return benefits?.export;
    if (featureName?.includes('caregiver')) return benefits?.caregiver;
    if (featureName?.includes('analytics')) return benefits?.analytics;
    return benefits?.default;
  };

  // Determine referral messaging based on current count
  const getReferralMessage = () => {
    if (referralCount < 5) {
      const remaining = 5 - referralCount;
      return {
        headline: '💚 Earn it free — Refer 5 friends to unlock Doctor Consultation Summary',
        progress: `You've referred ${referralCount} friend${referralCount !== 1 ? 's' : ''} — ${remaining} more to go!`,
        target: 5
      };
    } else if (referralCount < 10) {
      const remaining = 10 - referralCount;
      return {
        headline: '💚 Earn it free — Refer 10 friends to unlock Full Premium',
        progress: `You've referred ${referralCount} friends — ${remaining} more to go!`,
        target: 10
      };
    }
    return null;
  };

  const referralMsg = getReferralMessage();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Upgrade to Premium</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Feature Restriction Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <div className="flex items-start space-x-4">
              <div className="text-blue-600 flex-shrink-0">
                {getFeatureIcon()}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {featureDescription || 'Premium Feature'}
                </h3>
                {limit && currentUsage !== undefined && (
                  <div className="mb-3">
                    <p className="text-gray-700 mb-2">
                      You have reached your Basic plan limit:
                    </p>
                    <div className="bg-white rounded-lg p-3 border border-blue-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-600">Current Usage</span>
                        <span className="font-semibold text-gray-900">
                          {currentUsage} / {limit}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${Math.min((currentUsage / limit) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                <p className="text-gray-700">
                  Upgrade to Premium to unlock unlimited access and advanced features.
                </p>
              </div>
            </div>
          </div>

          {/* Premium Benefits */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              What you will get with Premium:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {getFeatureBenefits()?.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Plan Options */}
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Free Forever Plan */}
              <div className="border-2 border-gray-200 rounded-lg p-6">
                <h4 className="text-xl font-bold text-gray-900 mb-2">Free Forever</h4>
                <p className="text-gray-600 text-sm mb-4">Basic health tracking with essential features</p>

                <div className="mb-4">
                  <div className="text-3xl font-bold text-gray-900">
                    £0
                    <span className="text-lg text-gray-600 font-normal">/month</span>
                  </div>
                </div>

                <button
                  onClick={onClose}
                  className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
                >
                  Continue with Free
                </button>

                <div className="mt-4 space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                    Daily Check-in
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                    Symptom History
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                    Basic Health Tracking
                  </div>
                </div>
              </div>

              {/* Premium Plan */}
              <div className="border-2 border-blue-500 rounded-lg p-6 hover:border-blue-600 transition-all relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full">Recommended</span>
                </div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">Premium</h4>
                <p className="text-gray-600 text-sm mb-4">Full access to all health management features</p>

                <div className="mb-4">
                  <div className="text-3xl font-bold text-gray-900">
                    £4.99
                    <span className="text-lg text-gray-600 font-normal">/month</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    trackFeatureGateUpgradeClick(featureName || 'unknown');
                    onUpgrade?.('premium');
                    onClose();
                  }}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center"
                >
                  Upgrade to Premium £4.99/month
                  <ArrowRight className="w-5 h-5 ml-2" />
                </button>

                <div className="mt-4 space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                    Doctor Consultation Summary
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                    Patient Notes
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                    Trigger Tracking
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-600 mr-2 flex-shrink-0" />
                    Doctor Appointment Reminders
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Referral Free Path */}
          {!referralLoading && referralMsg && (
            <div className="mt-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider">OR</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                <div className="flex items-start gap-3 mb-3">
                  <Gift className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-green-800 font-semibold text-sm leading-snug">
                      {referralMsg?.headline}
                    </p>
                    <p className="text-green-700 text-sm mt-1">
                      {referralMsg?.progress}
                    </p>
                    {/* Inline progress bar */}
                    <div className="mt-3">
                      <div className="w-full bg-green-200 rounded-full h-2">
                        <div
                          className="bg-green-500 h-2 rounded-full transition-all"
                          style={{ width: `${Math.min((referralCount / referralMsg?.target) * 100, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-green-600 mt-1">
                        <span>{referralCount} referred</span>
                        <span>{referralMsg?.target} needed</span>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    trackFeatureGateReferClick(featureName || 'unknown');
                    onClose();
                    navigate('/referral-program');
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white py-2.5 px-4 rounded-lg font-semibold text-sm transition-colors flex items-center justify-center gap-2"
                >
                  <Gift className="w-4 h-4" />
                  Refer Now →
                </button>
              </div>
            </div>
          )}

          {/* Alternative Action */}
          <div className="mt-6 text-center">
            <button
              onClick={onClose}
              className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureGateModal;